import {
  ATC_CLASS_LABEL,
  ATC_CLASS_SHORT_LABEL,
  ATC_STATUS_LABEL,
  DEPARTMENT_CLASS_LABEL,
  DEPARTMENT_STATUS_LABEL,
  POSITION_STATUS_LABEL,
  type AtcClass,
  type AtcRow,
  type Department,
  type PersonLedgerRow,
  type PositionWithReadiness,
} from '@capsio/contracts';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { api } from '../api-client.js';
import { ROOT_ID, type AtcFilter } from '../app/node-tree-path.js';
import {
  LedgerFormPanel,
  type LedgerActions,
  type PanelTarget,
} from './ledger-forms.js';

/**
 * 「ATC 设置」页签 = **资产台账**（原型 v0.22 `setupHTML` / `renderSetupTable`）。
 *
 * 四张资产表各一个子页签：ATC 台账 / 岗位台账 / 部门台账 / 人员台账。
 *
 * ## 这一页是业务侧录入真实数据的**唯一入口**
 *
 * 不是查阅页。两个入口就是界面层的 SSOT 违规——两处行为一旦不同就成了两套真相，
 * 且录入本身是对这个工具的验证，走旁路等于跳过验证。
 *
 * ## 表格的真源是**领域接口**，不是树投影
 *
 * `api.departments()` / `api.positions()` / `api.atcs()` / `api.people()`。
 *
 * **不得再从 `TreeNode.attributes` 扩字段来凑表。** 岗位十一列、部门十二列若都从
 * `/api/tree` 的投影里扩，树投影会变成一个什么都装的大对象——而它的职责是
 * 「左栏那棵树要显示什么」，不是「所有表格要显示什么」。领域接口摆在那儿，
 * 完整字段与写路径都在上面。
 *
 * 树只剩两件事：**左栏联动**（`selectedId` 从哪儿来）与 `onSelect`（点了跳哪儿去）。
 * 所以这个组件**根本不收 `TreeIndex`**——收了就会有人再从里面取数。
 * 收窄靠 id 对得上：树节点的 id 就是 `department_id` / `position_id` / `atc_id`
 * 本身（投影视图里 `d.department_id::text AS id`），不需要另做一层映射。
 *
 * ## 哪几列不是真数据，以及为什么不编
 *
 * | 列 | 状况 |
 * |---|---|
 * | **运行**（五色灯） | 真源是任务执行记录，线上零条 → **零态**。不拿生命周期状态去点运行灯，那是两件事 |
 * | **达成率** | 部门级与岗位级的口径未建 → **零态** |
 *
 * 零态显示「—」。**不编数**：编一个 0% 出来，看的人分不清是「没达成」还是「算不出」。
 */

type LedgerTab = 'atc' | 'pos' | 'dep' | 'person';

const PAGE = 12;

/** 四张表的数据。**全部来自领域接口**——组件不认识 `TreeNode`。 */
export type LedgerData = {
  departments: Department[];
  positions: PositionWithReadiness[];
  atcs: AtcRow[];
  people: PersonLedgerRow[];
};

const EMPTY: LedgerData = { departments: [], positions: [], atcs: [], people: [] };

function hit(query: string, ...fields: (string | null)[]): boolean {
  if (query === '') return true;
  const q = query.toLowerCase();
  return fields.some((f) => (f ?? '').toLowerCase().includes(q));
}

/* ═══════ 收窄（A4） ═══════
 *
 * 选中左栏哪一个节点，四张台账就收到哪个范围，四张统计卡同步。
 *
 * | 选中 | ATC 台账 | 岗位台账 | 部门台账 | 人员台账 |
 * |---|---|---|---|---|
 * | 企业 | 全部 | 全部 | 全部 | 全部 |
 * | 部门 | 该部门下全部 | 该部门的岗位 | 该部门一行 | 该部门的人 |
 * | 岗位 | 该岗位名下 | 该岗位一行 | 所属部门一行 | 该岗位的人 |
 * | ATC | 该卡一行 | 责任岗位一行 | 所属部门一行 | 该卡关联的人 |
 *
 * **范围由 id 在哪张领域表里出现决定**，不看树上的 typeCode——
 * 树若因为投影缺字段而认不出某个节点，这里仍然认得出。
 */
type Scope =
  | { kind: 'enterprise' }
  | { kind: 'department'; departmentId: string; label: string }
  | { kind: 'position'; positionId: string; departmentId: string | null; label: string }
  | {
      kind: 'atc';
      atcId: string;
      positionId: string;
      departmentId: string | null;
      label: string;
    };

export function resolveScope(data: LedgerData, selectedId: string): Scope {
  if (selectedId === ROOT_ID) return { kind: 'enterprise' };

  const dept = data.departments.find((d) => d.id === selectedId);
  if (dept !== undefined)
    return { kind: 'department', departmentId: dept.id, label: `部门「${dept.name}」` };

  const pos = data.positions.find((p) => p.id === selectedId);
  if (pos !== undefined)
    return {
      kind: 'position',
      positionId: pos.id,
      departmentId: pos.departmentId,
      label: `岗位「${pos.name}」`,
    };

  const atc = data.atcs.find((a) => a.id === selectedId);
  if (atc !== undefined) {
    const owner = data.positions.find((p) => p.id === atc.positionId);
    return {
      kind: 'atc',
      atcId: atc.id,
      positionId: atc.positionId,
      departmentId: owner?.departmentId ?? null,
      label: `ATC「${atc.name}」`,
    };
  }

  /*
    选中项在四张领域表里都找不到——比如刚被别人删掉，或者选的是一个还没加载完的 id。
    退回企业级：范围失效不该让整页空白，退到全量是唯一不需要额外文案的兜底。
  */
  return { kind: 'enterprise' };
}

/** 收窄之后的四张表。**一处算好，表格与统计卡共用**——两处各筛一遍必然给出两个数。 */
export function narrow(data: LedgerData, scope: Scope, clsFilter: AtcFilter): LedgerData {
  const scoped = narrowByScope(data, scope);
  if (clsFilter === 'all') return scoped;

  /*
    共用筛选下的整枝裁剪，**与左栏树同一条规则**（unitChildrenOf 的注）：
    部门按自己的 cls，岗位与人员随所属部门，ATC 逐卡且须落在保留的枝上。
    树上消失的枝，台账里同样不出现——同一时刻两边是同一张图。
  */
  const deptOk = new Set(data.departments.filter((d) => d.cls === clsFilter).map((d) => d.id));
  const deptOfPosition = new Map(data.positions.map((p) => [p.id, p.departmentId]));
  return {
    departments: scoped.departments.filter((d) => deptOk.has(d.id)),
    positions: scoped.positions.filter((p) => deptOk.has(p.departmentId)),
    atcs: scoped.atcs.filter(
      (a) => a.cls === clsFilter && deptOk.has(deptOfPosition.get(a.positionId) ?? ''),
    ),
    people: scoped.people.filter((r) => deptOk.has(r.departmentId ?? '')),
  };
}

/** 按左栏选中节点收窄（范围）。类别筛选在其外层的 `narrow` 里叠加。 */
function narrowByScope(data: LedgerData, scope: Scope): LedgerData {
  switch (scope.kind) {
    case 'enterprise':
      return data;

    case 'department': {
      const positions = data.positions.filter((p) => p.departmentId === scope.departmentId);
      const ids = new Set(positions.map((p) => p.id));
      return {
        departments: data.departments.filter((d) => d.id === scope.departmentId),
        positions,
        atcs: data.atcs.filter((a) => ids.has(a.positionId)),
        people: data.people.filter((r) => r.departmentId === scope.departmentId),
      };
    }

    case 'position':
      return {
        departments: data.departments.filter((d) => d.id === scope.departmentId),
        positions: data.positions.filter((p) => p.id === scope.positionId),
        atcs: data.atcs.filter((a) => a.positionId === scope.positionId),
        people: data.people.filter((r) => r.positionId === scope.positionId),
      };

    case 'atc': {
      const atcs = data.atcs.filter((a) => a.id === scope.atcId);
      return {
        departments: data.departments.filter((d) => d.id === scope.departmentId),
        positions: data.positions.filter((p) => p.id === scope.positionId),
        atcs,
        /* 「该卡关联的人」——挂在这张卡上的，不是这个岗位上的。两者不同：
           一个岗位上的人未必都挂了这张卡。 */
        people: data.people.filter((r) => r.atcIds.includes(scope.atcId)),
      };
    }

    default: {
      /* 穷尽检查：将来加一种范围而忘了在这里处理，编译期就红。 */
      const never: never = scope;
      return never;
    }
  }
}

export function AssetLedger({
  selectedId,
  onSelect,
  clsFilter,
  onClsFilterChange,
  onChanged,
  onEditAtc,
}: {
  selectedId: string;
  onSelect: (id: string) => void;
  /**
   * ATC 台账「修改」的落点（C-190 批三 A8）：跳卡页并直接进入编辑态，
   * 不再在台账弹两字段的表单（那个表单只有编号+名称，而卡页编辑态改五项——
   * 同一字段两个入口，且一个只能改五分之一）。所见即所得。
   * 停用/启用不走这里：状态动作留在台账。
   */
  onEditAtc?: (id: string) => void;
  /** 运营/建设筛选。**与左栏共用一份状态**（补-B 联动）：筛选是换一张图看，图只有一张。 */
  clsFilter: AtcFilter;
  onClsFilterChange: (next: AtcFilter) => void;
  /** 写操作成功后通知外层（重拉树等）。A6 的「资产树已联动」靠它兑现。 */
  onChanged: () => void;
}): ReactNode {
  const [data, setData] = useState<LedgerData>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const [departments, positions, atcs, people] = await Promise.all([
      api.departments(),
      api.positions(),
      api.atcs(),
      api.people(),
    ]);
    setData({
      departments: departments.rows,
      positions: positions.rows,
      atcs: atcs.rows,
      people: people.rows,
    });
  }, []);

  useEffect(() => {
    let live = true;
    void Promise.all([api.departments(), api.positions(), api.atcs(), api.people()])
      .then(([departments, positions, atcs, people]) => {
        if (!live) return;
        setData({
          departments: departments.rows,
          positions: positions.rows,
          atcs: atcs.rows,
          people: people.rows,
        });
      })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : '读取失败');
      });
    return () => {
      live = false;
    };
    // 初次装载故意与 load() 分开写：effect 里调一个会 setState 的 useCallback
    // 会被 react-hooks/set-state-in-effect 拦（级联渲染风险）。load 只服务写后重拉。
  }, []);

  /** 写后两件事：台账自己重拉，外层（树）联动。 */
  const afterWrite = async (): Promise<void> => {
    await load();
    onChanged();
  };

  /*
   * 写路径（A1/A2/A5/A6）。表单只收集值，落库在这里——
   * 交接节那条缝：提交回调上提取数壳，渲染那半继续可判。
   */
  const actions: LedgerActions = {
    async saveDepartment(v) {
      if (v.id === undefined) {
        await api.addDepartment({
          name: v.name,
          cls: v.cls,
          headRef: v.headRef,
          charter: v.charter,
          headcountSurveyed: v.headcountSurveyed,
          sourceNote: v.sourceNote,
        });
        await afterWrite();
        return `已新增部门「${v.name}」，资产树已联动。`;
      }
      await api.updateDepartment(v.id, {
        name: v.name,
        cls: v.cls,
        headRef: v.headRef,
        charter: v.charter,
        headcountSurveyed: v.headcountSurveyed,
        sourceNote: v.sourceNote,
      });
      await afterWrite();
      return `已修改部门「${v.name}」。`;
    },

    async savePosition(v) {
      // 自由文本 → 对象。空就是不填（「{}」是合法的初始值）
      const authorityScope = v.authorityScopeText === '' ? {} : { 内容: v.authorityScopeText };
      const competencyCriteria = v.competencyText === '' ? {} : { 内容: v.competencyText };
      if (v.id === undefined) {
        const created = await api.addPosition({
          departmentId: v.departmentId ?? '',
          code: v.code,
          name: v.name,
          mission: v.mission,
          responsibility: v.responsibility,
          authorityScope,
          competencyCriteria,
          plannedCardCount: v.plannedCardCount,
        });
        /* 编制人数与卡位一次填完（T 第 2 步）：建岗后紧跟一次设定。
           它是收口 + 追加（position_headcount 带区间），不是就地改字段。 */
        if (v.headcount !== null && v.headcount !== undefined) {
          await api.setHeadcount(created.id, { headcount: v.headcount });
        }
        await afterWrite();
        const deptName =
          data.departments.find((d) => d.id === v.departmentId)?.name ?? '';
        return `已在「${deptName}」下新增岗位「${v.name}」。`;
      }
      await api.updatePosition(v.id, {
        name: v.name,
        mission: v.mission,
        responsibility: v.responsibility,
        // 留空 = 不动原值（表单预填不了对象，也不该把 JSON 怼给人看）
        ...(v.authorityScopeText === '' ? {} : { authorityScope }),
        ...(v.competencyText === '' ? {} : { competencyCriteria }),
        plannedCardCount: v.plannedCardCount,
      });
      await afterWrite();
      return `已修改岗位「${v.name}」。`;
    },

    async saveAtc(v) {
      if (v.id === undefined) {
        const pos = data.positions.find((p) => p.id === v.positionId);
        const dept = data.departments.find((d) => d.id === pos?.departmentId);
        // cls 跟着部门走（服务端也会再校一遍，不一致是 400 而不是静默纠正）
        const created = await api.addAtc({
          positionId: v.positionId ?? '',
          name: v.name,
          cls: dept?.cls ?? 'operational',
          ownerScope: 'client',
        });
        await afterWrite();
        return `已新增「${v.name}」（${created.code}，draft）。资产树与热力图已联动。`;
      }
      await api.updateAtc(v.id, { name: v.name });
      await afterWrite();
      return `已修改「${v.name}」。`;
    },

    async setStatus(kind, id, next, reason) {
      if (kind === 'department') await api.changeDepartmentStatus(id, next, reason);
      else if (kind === 'position') await api.changePositionStatus(id, next, reason);
      else await api.changeAtcStatus(id, next, reason);
      await afterWrite();
      return next === 'active' ? '已启用。' : '已停用。';
    },

    async savePerson(v) {
      if (v.employeeNo === undefined) {
        const created = await api.addPerson({
          name: v.name,
          externalRef: v.externalRef === '' ? undefined : v.externalRef,
          employedFrom: v.employedFrom,
          positionId: v.positionId ?? '',
        });
        await afterWrite();
        return `已新增人员「${v.name}」（工号 ${created.employeeNo}），挂在「${created.positionName ?? ''}」。`;
      }
      await api.updatePerson(v.employeeNo, {
        name: v.name,
        externalRef: v.externalRef === '' ? null : v.externalRef,
      });
      await afterWrite();
      return `已修改人员「${v.name}」。`;
    },

    async reassignPerson(employeeNo, positionId, validFrom) {
      const row = await api.reassignPerson(employeeNo, { positionId, validFrom });
      await afterWrite();
      return `已调岗：「${row.name}」现属「${row.positionName ?? ''}」（${validFrom} 起）。`;
    },

    async departPerson(employeeNo, employedTo) {
      const row = await api.departPerson(employeeNo, { employedTo });
      await afterWrite();
      return `已登记离职：「${row.name}」（${employedTo}）。`;
    },
  };

  return (
    <LedgerView
      data={data}
      selectedId={selectedId}
      onSelect={onSelect}
      error={error}
      clsFilter={clsFilter}
      onClsFilterChange={onClsFilterChange}
      actions={actions}
      onEditAtc={onEditAtc}
    />
  );
}

/**
 * 纯渲染那一半。**不取数、不认识 `api`**——所以判定能直接喂四张表进来，
 * 不需要 jsdom 也不需要打桩网络。
 */
export function LedgerView({
  data,
  selectedId,
  onSelect,
  error = null,
  clsFilter,
  onClsFilterChange,
  actions,
  onEditAtc,
}: {
  data: LedgerData;
  selectedId: string;
  onSelect: (id: string) => void;
  error?: string | null;
  clsFilter: AtcFilter;
  onClsFilterChange: (next: AtcFilter) => void;
  /**
   * 写路径（A1/A2/A5）。取数壳给，判定可以不给——不给时工具条没有「＋新增」、
   * 操作列只剩「查看」，整页退回纯台账。
   */
  actions?: LedgerActions;
  /**
   * ATC 台账「修改」的落点（C-190 批三 A8）：跳卡页并直接进入编辑态。
   * 不给时退回旧行为（本页弹两字段表单）——判定场景不挂在卡页上。
   */
  onEditAtc?: (id: string) => void;
}): ReactNode {
  const [tab, setTab] = useState<LedgerTab>('atc');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  /** 展开中的新增/修改面板；null = 收着。 */
  const [panel, setPanel] = useState<PanelTarget | null>(null);
  /** 回执（A6）：上一步做了什么，一句话。 */
  const [notice, setNotice] = useState<string | null>(null);
  /*
    运营 / 建设筛选**是外面传进来的共用状态**（左栏 `.flt` 与这里的三档
    读写同一份）：筛选是换一张图看，图只有一张。接 `atc.cls`，不接
    `owner_scope`——后者是「客户的卡 / 我方的卡」，是另一个轴。
  */

  const scope = resolveScope(data, selectedId);
  const shown = narrow(data, scope, clsFilter);

  function switchTab(next: LedgerTab): void {
    setTab(next);
    // 换表就回第一页：留在第 5 页而新表只有 2 页，看到的是一张空表而不报错
    setPage(1);
    // 面板跟着当前页签走，换页签就收起来——不然在岗位台账上开着部门的表单
    setPanel(null);
    /*
      **筛选档不复位。** 它现在是与左栏共用的视图状态：左栏还停在「建设」，
      这里悄悄弹回「全部」，同一屏就出现两张图。筛选在哪儿亮着，
      当前范围那行写明（见下）。
    */
  }

  /** 状态切换（A2/A7）：全部走三个统一入口，不新开路径。 */
  function toggleStatus(kind: 'department' | 'position' | 'atc', id: string, current: string): void {
    if (actions === undefined) return;
    const next = nextStatusOf(kind, current);
    if (next === null) return;
    // 停用/退役必须留一句为什么（服务端也强制）；启用不需要
    const reason =
      next === 'active'
        ? ''
        : (window.prompt('写一句为什么（停用会留痕，三个月后要知道为什么）', '台账页操作') ?? '');
    if (next !== 'active' && reason.trim() === '') return;
    void actions
      .setStatus(kind, id, next, reason)
      .then(setNotice)
      .catch((e: unknown) => setNotice(e instanceof Error ? e.message : '操作失败'));
  }

  const tableExtras = {
    onEdit:
      actions === undefined
        ? undefined
        : (kind: 'dep' | 'pos' | 'atc') => (id: string) =>
            /* ATC 的「修改」跳卡页编辑态（A8）；部门/岗位仍走本页表单。 */
            kind === 'atc' && onEditAtc !== undefined ? onEditAtc(id) : setPanel({ kind, id }),
    onToggleStatus: actions === undefined ? undefined : toggleStatus,
    onPersonAction:
      actions === undefined
        ? undefined
        : (mode: 'edit' | 'move' | 'leave') => (employeeNo: string) =>
            setPanel({ kind: 'person', id: employeeNo, mode }),
  };

  /* 统计卡的数**用收窄之后的那一份**（A4：四张统计卡同步收窄）。 */
  const counts = {
    dep: shown.departments.length,
    pos: shown.positions.length,
    atc: shown.atcs.length,
    operational: shown.atcs.filter((a) => a.cls === 'operational').length,
    construction: shown.atcs.filter((a) => a.cls === 'construction').length,
    /*
      待补全 = 状态为 draft 的卡数。**不是「停用」**——第四格问的是
      「还有多少张卡的四段与 TEST 没写完」，那是建设工作量；
      停用是另一件事，且它已经在每一行的状态列上。
    */
    draft: shown.atcs.filter((a) => a.status === 'draft').length,
  };

  return (
    /*
      **整块必须包在 `.pv22` 里**：`grid4`、`card`、`table.tb`、`badge`、`pct`
      这些类全部只存在于 `prototype-v022.css`，而那份是按 `.pv22` 作用域生成的。
      不包的话页面**不报错、只是全无样式**——四个统计格会竖成一列、表格没有线。
    */
    <div className="pv22">
      <div className="grid4">
        <LedgerStat label="部门" big={counts.dep} unit="个" aux="资产树第一层" />
        <LedgerStat label="岗位" big={counts.pos} unit="个" aux="岗位职能体，资产树第二层" />
        <LedgerStat
          label="ATC"
          big={counts.atc}
          unit="张（已建模）"
          aux={`运营 ${counts.operational} · 建设 ${counts.construction}`}
        />
        <LedgerStat
          label="待补全"
          big={counts.draft}
          unit="张 draft"
          aux="TEST 为空不得 certified"
        />
      </div>

      <div className="card">
        <h3>
          资产台账{' '}
          <span className="sub">
            四张资产表：部门 / 岗位 / ATC / 人员 · 新增即联动资产树与热力图 ·
            管理端录入，非任务操作界面
          </span>
        </h3>

        {/*
          收窄状态要看得见（A4）。**一行说明，不是一个角标**——
          「为什么这张表只有三行」必须能一眼答上来，否则会被当成数据丢了。
        */}
        <p className="note">
          当前范围：{scope.kind === 'enterprise' ? '全企业' : scope.label}
          {scope.kind !== 'enterprise' && '  （点左栏企业节点回到全部）'}
          {/* 筛选是与左栏共用的视图状态，在哪儿生效就在哪儿写明——
              不然「这张表怎么少了行」答不上来。 */}
          {clsFilter !== 'all' && `  · 只看「${ATC_CLASS_LABEL[clsFilter]}」`}
        </p>

        {error !== null && <p className="note">读取失败：{error}</p>}

        <div className="stabs">
          {(
            [
              ['atc', 'ATC 台账'],
              ['pos', '岗位台账'],
              ['dep', '部门台账'],
              ['person', '人员台账'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={tab === key ? 'on' : ''}
              onClick={() => switchTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="tbar">
          <input
            type="text"
            placeholder="表内快速筛选…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value.trim());
              setPage(1);
            }}
          />
          {/*
            类别三档**只在 ATC 台账出现**：`cls` 是 ATC 的列，其余三张表上没有这个轴。
            在那几页放出来，等于给一个筛不动的控件。
          */}
          {tab === 'atc' &&
            (
              [
                ['all', '全部'],
                ['operational', ATC_CLASS_SHORT_LABEL.operational],
                ['construction', ATC_CLASS_SHORT_LABEL.construction],
              ] as const
            ).map(([key, label]) => (
              /*
                用 `.btns`，**不用侧栏那个 `.flt`**：那条规则写作
                `.pv22 .side .flt`，只在侧栏子树里生效。套在这儿类名对得上、
                样式一条都不落——不报错、只是三个按钮长得像三段裸文字。
              */
              <button
                key={key}
                type="button"
                aria-label={`按类别筛选：${label}`}
                className={clsFilter === key ? 'btns on' : 'btns'}
                onClick={() => {
                  onClsFilterChange(key);
                  // 换档回第一页：第 3 页的内容筛完可能已经不存在
                  setPage(1);
                }}
              >
                {label}
              </button>
            ))}
          {/* 把右端留空。原型 `.tbar` 里就是这一个空 span。 */}
          <span style={{ flex: 1 }} />
          {actions !== undefined && (
            <button
              type="button"
              className="btns"
              aria-label={`新增${tab === 'atc' ? 'ATC' : tab === 'pos' ? '岗位' : tab === 'dep' ? '部门' : '人员'}`}
              onClick={() => {
                const kind = tab === 'atc' ? 'atc' : tab === 'pos' ? 'pos' : tab === 'dep' ? 'dep' : 'person';
                setPanel(kind === 'person' ? { kind: 'person' } : { kind });
              }}
            >
              ＋新增
            </button>
          )}
        </div>

        {notice !== null && <p className="note">{notice}</p>}

        {actions !== undefined && panel !== null && (
          <LedgerFormPanel
            target={panel}
            data={data}
            actions={actions}
            onDone={(message) => {
              setPanel(null);
              setNotice(message);
            }}
            onCancel={() => setPanel(null)}
          />
        )}

        {tab === 'atc' && (
          <AtcTable
            data={shown}
            cls={clsFilter}
            query={query}
            page={page}
            setPage={setPage}
            onSelect={onSelect}
            onEdit={tableExtras.onEdit?.('atc')}
            onToggleStatus={tableExtras.onToggleStatus === undefined ? undefined : (id, status) => tableExtras.onToggleStatus?.('atc', id, status)}
          />
        )}
        {tab === 'pos' && (
          <PosTable
            data={shown}
            query={query}
            page={page}
            setPage={setPage}
            onSelect={onSelect}
            onEdit={tableExtras.onEdit?.('pos')}
            onToggleStatus={tableExtras.onToggleStatus === undefined ? undefined : (id, status) => tableExtras.onToggleStatus?.('position', id, status)}
          />
        )}
        {tab === 'dep' && (
          <DeptTable
            data={shown}
            query={query}
            page={page}
            setPage={setPage}
            onSelect={onSelect}
            onEdit={tableExtras.onEdit?.('dep')}
            onToggleStatus={tableExtras.onToggleStatus === undefined ? undefined : (id, status) => tableExtras.onToggleStatus?.('department', id, status)}
          />
        )}
        {tab === 'person' && (
          <PersonTable
            data={shown}
            query={query}
            page={page}
            setPage={setPage}
            onSelect={onSelect}
            onPersonAction={tableExtras.onPersonAction}
          />
        )}

        {/*
          零态两列的交代。**写在表下面而不是每格一个问号**——
          十二列里有两列是空的，看的人第一反应是「数据没加载出来」。
        */}
        {/*
          强调用 <b>，**不要写 Markdown 的星号**——JSX 里的星号是**字面量**，
          屏幕上就是两个星号。这一条是部署后看截图才发现的：注释里满篇 `**`
          写惯了，顺手带进了 JSX 文本，而它不报错、类型也对。
        */}
        <p className="note">
          「运行」与「达成率」两列显示「—」：前者的真源是任务执行记录，线上尚无一条；
          后者的部门级与岗位级口径未建。<b>不编数</b>——编一个 0 出来，
          看的人分不清是「真的是零」还是「算不出来」。
        </p>
      </div>
    </div>
  );
}

function LedgerStat({
  label,
  big,
  unit,
  aux,
}: {
  label: string;
  big: number;
  unit: string;
  aux: string;
}): ReactNode {
  return (
    <div className="card stat">
      <div className="lbl">{label}</div>
      <div className="big">
        {big}
        <small>{unit}</small>
      </div>
      <div className="aux">{aux}</div>
    </div>
  );
}

function Pager({
  total,
  page,
  setPage,
}: {
  total: number;
  page: number;
  setPage: (n: number) => void;
}): ReactNode {
  const pages = Math.max(1, Math.ceil(total / PAGE));
  return (
    <div className="pager">
      <span>共 {total} 条</span>
      <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>
        上一页
      </button>
      <span>
        页 {Math.min(page, pages)} / {pages}
      </span>
      <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)}>
        下一页
      </button>
    </div>
  );
}

function slice<T>(items: T[], page: number): T[] {
  const pages = Math.max(1, Math.ceil(items.length / PAGE));
  const p = Math.min(page, pages);
  return items.slice((p - 1) * PAGE, p * PAGE);
}

/**
 * 长文本在表格里截断。
 *
 * **不用 CSS 省略号**：`prototype-v022.css` 是机械生成的，里面没有 `.ell` 这类规则，
 * 而套一个不存在的类名**不报错、只是一条样式都不落**——一段两千字的职能简述
 * 会把整张表撑成一列。判定判的是字符数，不是「看起来对不对」。
 */
function ellipsis(text: string, max = 40): string {
  if (text === '') return '—';
  return text.length <= max ? text : `${text.slice(0, max)}…`;
}

/** 零态的那一格。**「—」不是 0**——见页尾那段说明。 */
function Zero(): ReactNode {
  return <span className="mono">—</span>;
}

/**
 * 状态徽标。**徽标形状，不是按钮形状**（A5）。
 *
 * 此前部门台账的「启用」做成了绿色圆角按钮样式却不可点——
 * **长得像按钮而不能点，比徽标更坏**。操作一律在独立的「操作」列里。
 */
function StatusBadge({ label, tone }: { label: string; tone: string }): ReactNode {
  return <span className={`badge ${tone}`}>{label}</span>;
}

/** 系统对象的标。与状态**分列**——「系统」不是一种状态，它是这个对象的出身。 */
function SystemMark({ isSystem }: { isSystem: boolean }): ReactNode {
  if (!isSystem) return null;
  return <span className="badge blu">系统</span>;
}

function ClassBadge({ cls }: { cls: AtcClass }): ReactNode {
  return (
    <span className={`badge ${cls === 'construction' ? 'pur' : 'blu'}`}>
      {ATC_CLASS_SHORT_LABEL[cls]}
    </span>
  );
}

/** 「查看」跳到那个节点。 */
function ViewButton({ id, onSelect }: { id: string; onSelect: (id: string) => void }): ReactNode {
  return (
    <button type="button" className="linkbtn" onClick={() => onSelect(id)}>
      查看
    </button>
  );
}

/**
 * 停用/启用的下一个状态（A7）。**部门没有 suspended**——库层 CHECK 只认
 * active / merged / retired，「停用」一个部门落的是 retired（界面上叫「已停用」，
 * 既有产品词汇）。不在这条边上的状态（draft、proposed 等）不给停用按钮——
 * 给一个注定被服务端拒掉的按钮，比不给更难解释。
 */
function nextStatusOf(kind: 'department' | 'position' | 'atc', current: string): string | null {
  if (kind === 'department') {
    return current === 'active' ? 'retired' : current === 'retired' ? 'active' : null;
  }
  return current === 'active' ? 'suspended' : current === 'suspended' ? 'active' : null;
}

type TableProps = {
  data: LedgerData;
  query: string;
  page: number;
  setPage: (n: number) => void;
  onSelect: (id: string) => void;
  /** 打开修改面板。不给则操作列只剩「查看」。 */
  onEdit?: (id: string) => void;
  onToggleStatus?: (id: string, current: string) => void;
};

/* ═══════ ATC 台账 ═══════ */

export function AtcTable({
  data,
  cls,
  query,
  page,
  setPage,
  onSelect,
  onEdit,
  onToggleStatus,
}: TableProps & { cls: 'all' | AtcClass }): ReactNode {
  const nameOf = (id: string | null | undefined, list: { id: string; name: string }[]): string =>
    list.find((x) => x.id === id)?.name ?? '—';

  const list = data.atcs
    .filter((a) => cls === 'all' || a.cls === cls)
    .map((atc) => {
      const pos = data.positions.find((p) => p.id === atc.positionId);
      return {
        atc,
        posName: pos?.name ?? '—',
        depName: nameOf(pos?.departmentId, data.departments),
      };
    })
    .filter(({ atc, posName, depName }) => hit(query, atc.name, atc.code, posName, depName))
    /* 排序按编号升序。没编号的排在最后而不是最前。 */
    .sort((a, b) => (a.atc.code || '￿').localeCompare(b.atc.code || '￿'));

  return (
    <>
      <table className="tb">
        <thead>
          <tr>
            <th>编号</th>
            <th>名称</th>
            <th>类别</th>
            <th>部门</th>
            <th>责任岗位</th>
            <th>状态</th>
            <th>运行</th>
            <th>成熟度</th>
            <th>TEST</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {slice(list, page).map(({ atc, posName, depName }) => (
            <tr key={atc.id}>
              <td className="mono">{atc.code || '—'}</td>
              <td>
                <button type="button" className="linkbtn" onClick={() => onSelect(atc.id)}>
                  {atc.name}
                </button>
              </td>
              <td>
                <ClassBadge cls={atc.cls} />
              </td>
              <td>{depName}</td>
              <td>{posName}</td>
              <td>
                {/*
                  **生命周期状态**，中文。与「运行」是两列两件事——
                  拿生命周期去点运行灯会显示错的语义。
                */}
                <StatusBadge
                  label={ATC_STATUS_LABEL[atc.status as keyof typeof ATC_STATUS_LABEL] ?? atc.status}
                  tone={atcTone(atc.status)}
                />
              </td>
              <td>
                {/* 真源是任务执行记录，线上零条。**零态，不编数。** */}
                <Zero />
              </td>
              <td className="mono">{atc.maturityLevel}</td>
              <td>
                {/*
                  有无由**库**判（`test_spec <> '{}'`，与 C-06 那条约束同源）。
                  前端不重判——前端另判一遍会在 `{"steps": []}` 这类取值上与库分叉。
                */}
                {atc.hasTest ? (
                  <span className="badge ok">✓</span>
                ) : (
                  <span className="badge red">缺</span>
                )}
              </td>
              <td>
                <ViewButton id={atc.id} onSelect={onSelect} />
                {onEdit !== undefined && (
                  <button type="button" className="linkbtn" onClick={() => onEdit(atc.id)}>
                    修改
                  </button>
                )}
                {onToggleStatus !== undefined && nextStatusOf('atc', atc.status) !== null && (
                  <button
                    type="button"
                    className={`linkbtn ${atc.status === 'suspended' ? 'res' : 'warn'}`}
                    onClick={() => onToggleStatus(atc.id, atc.status)}
                  >
                    {atc.status === 'suspended' ? '启用' : '停用'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager total={list.length} page={page} setPage={setPage} />
    </>
  );
}

/** ATC 七态的徽标色。停用与退役压灰，其余按推进程度。 */
function atcTone(status: string): string {
  if (status === 'active' || status === 'certified') return 'ok';
  if (status === 'suspended' || status === 'retired') return 'gry';
  return 'amb';
}

/* ═══════ 岗位台账 ═══════ */

export function PosTable({ data, query, page, setPage, onSelect, onEdit, onToggleStatus }: TableProps): ReactNode {
  const list = data.positions
    .map((pos) => {
      const dept = data.departments.find((d) => d.id === pos.departmentId);
      return { pos, dept };
    })
    .filter(({ pos, dept }) => hit(query, pos.name, pos.code, dept?.name ?? null))
    .sort((a, b) => (a.pos.code || '￿').localeCompare(b.pos.code || '￿'));

  return (
    <>
      <table className="tb">
        <thead>
          <tr>
            <th>岗位</th>
            <th>编码</th>
            <th>所属部门</th>
            <th>类别</th>
            <th>已建模 ATC</th>
            <th>卡位总数</th>
            <th>空卡位</th>
            <th>在岗人数</th>
            <th>达成率</th>
            <th>状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {slice(list, page).map(({ pos, dept }) => (
            <tr key={pos.id}>
              <td>
                <button type="button" className="linkbtn" onClick={() => onSelect(pos.id)}>
                  <b>{pos.name}</b>
                </button>{' '}
                <SystemMark isSystem={pos.isSystem} />
              </td>
              <td className="mono">{pos.code || '—'}</td>
              <td>{dept?.name ?? '—'}</td>
              <td>
                {/* 岗位的类别**随所属部门**，本身没有独立字段——多一列就多一处能不一致。 */}
                {dept === undefined ? '—' : DEPARTMENT_CLASS_LABEL[dept.cls]}
              </td>
              <td className="mono">{pos.filledCardCount}</td>
              <td className="mono">
                {/* `null` = 还没调研过，**不折成 0**——0 是「调研过，结论是不需要卡」。 */}
                {pos.plannedCardCount === null ? '未调研' : pos.plannedCardCount}{' '}
                <OverMark over={pos.overCardCount} />
              </td>
              <td className="mono">
                {/* 库算，**下限 0**。超出的部分走上一列那个徽标，不在这里显示负数。 */}
                {pos.vacantCardCount === null ? '—' : pos.vacantCardCount}
              </td>
              <td className="mono">{pos.staffedCount}</td>
              <td>
                <Zero />
              </td>
              <td>
                <StatusBadge
                  label={POSITION_STATUS_LABEL[pos.status]}
                  tone={pos.status === 'active' ? 'ok' : pos.status === 'draft' ? 'amb' : 'gry'}
                />
              </td>
              <td>
                <ViewButton id={pos.id} onSelect={onSelect} />
                {pos.isSystem ? (
                  <span className="note">系统岗位不可修改 · 不可停用</span>
                ) : (
                  onEdit !== undefined && (
                    <button type="button" className="linkbtn" onClick={() => onEdit(pos.id)}>
                      修改
                    </button>
                  )
                )}
                {!pos.isSystem &&
                  onToggleStatus !== undefined &&
                  nextStatusOf('position', pos.status) !== null && (
                    <button
                      type="button"
                      className={`linkbtn ${pos.status === 'suspended' ? 'res' : 'warn'}`}
                      onClick={() => onToggleStatus(pos.id, pos.status)}
                    >
                      {pos.status === 'suspended' ? '启用' : '停用'}
                    </button>
                  )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager total={list.length} page={page} setPage={setPage} />
    </>
  );
}

/**
 * 「已超 N」（T-148）。
 *
 * **这是合法状态，不是错误。** 现实超出规划是常事，而且它本身是信息——
 * 这个岗位的活比当初规划的多。所以用**中性色**（`gry`），不用告警色。
 *
 * **MUST NOT**  不得据此把卡位总数自动上调到已立卡数。
 * 那是产品替客户改他的规划数——产品提供位置，不提供内容。
 */
function OverMark({ over }: { over: number | null }): ReactNode {
  if (over === null || over <= 0) return null;
  return <span className="badge gry">已超 {over}</span>;
}

/* ═══════ 部门台账 ═══════ */

export function DeptTable({ data, query, page, setPage, onSelect, onEdit, onToggleStatus }: TableProps): ReactNode {
  const list = data.departments
    .filter((d) => hit(query, d.name, d.code, d.headRef, d.sourceNote))
    .sort((a, b) => (a.code || '￿').localeCompare(b.code || '￿'));

  const atcCountOf = (departmentId: string): number => {
    const ids = new Set(
      data.positions.filter((p) => p.departmentId === departmentId).map((p) => p.id),
    );
    return data.atcs.filter((a) => ids.has(a.positionId)).length;
  };

  return (
    <>
      <table className="tb">
        <thead>
          <tr>
            <th>部门</th>
            <th>编码</th>
            <th>类别</th>
            <th>岗位数</th>
            <th>ATC 数</th>
            <th>部门负责人</th>
            <th>主要职能简述</th>
            <th>问卷填报人数</th>
            <th>来源标注</th>
            <th>达成率</th>
            <th>状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {slice(list, page).map((d) => (
            <tr key={d.id}>
              <td>
                <button type="button" className="linkbtn" onClick={() => onSelect(d.id)}>
                  <b>{d.name}</b>
                </button>{' '}
                <SystemMark isSystem={d.isSystem} />
              </td>
              <td className="mono">{d.code || '—'}</td>
              <td>{DEPARTMENT_CLASS_LABEL[d.cls]}</td>
              <td className="mono">{d.positionCount}</td>
              <td className="mono">{atcCountOf(d.id)}</td>
              <td>{d.headRef || '—'}</td>
              <td>{ellipsis(d.charter)}</td>
              <td className="mono">
                {/* `null` = 没填。**不要用 0 代表「没填」**——0 是「填了，是零个人」。 */}
                {d.headcountSurveyed === null ? '未填' : d.headcountSurveyed}
              </td>
              <td>
                {/*
                  **来源标注是判断这一行可信度的唯一线索，必须有。**
                  空着时明说「未标注」而不是显示一个「—」：后者看起来像「这一格没有内容」，
                  前者说的是「这一行的来路不明」——两句话分量不同。
                */}
                {d.sourceNote === '' ? <span className="badge amb">未标注</span> : d.sourceNote}
              </td>
              <td>
                <Zero />
              </td>
              <td>
                <StatusBadge
                  label={DEPARTMENT_STATUS_LABEL[d.status]}
                  tone={d.status === 'active' ? 'ok' : 'gry'}
                />
              </td>
              <td>
                <ViewButton id={d.id} onSelect={onSelect} />
                {d.isSystem ? (
                  <span className="note">系统部门不可修改 · 不可停用</span>
                ) : (
                  onEdit !== undefined && (
                    <button type="button" className="linkbtn" onClick={() => onEdit(d.id)}>
                      修改
                    </button>
                  )
                )}
                {!d.isSystem &&
                  onToggleStatus !== undefined &&
                  nextStatusOf('department', d.status) !== null && (
                    <button
                      type="button"
                      className={`linkbtn ${d.status === 'active' ? 'warn' : 'res'}`}
                      onClick={() => onToggleStatus(d.id, d.status)}
                    >
                      {/* 部门的「停用」落库是 retired——界面上叫「已停用」，既有产品词汇 */}
                      {d.status === 'active' ? '停用' : '启用'}
                    </button>
                  )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager total={list.length} page={page} setPage={setPage} />
    </>
  );
}

/* ═══════ 人员台账 ═══════ */

/**
 * 第四张台账。**形制与前三张不同，是有理由的：**
 *
 * 1. **行锚是工号**，不是「编码 + 名称」。工号全局唯一、永不复用、永不改；
 *    `externalRef` 是客户侧标识，可空可改，**不是锚点**。两者都显示，
 *    且在表头标明哪个是锚点——不标的话，换人事系统时没人知道该认哪一个。
 * 2. **岗位列只读。** 岗位不是人身上的字段，是在岗关系当前区间的投影。
 *    调岗是「闭合旧区间 + 追加新区间」，不是改这一格。历史在「在岗历史」里展开。
 * 3. **没有 active / suspended / retired 那一套。** 人只有在职与离职，
 *    由 `employedTo` 一个字段表达，且它一次性写入。
 *    **不出现「归档」「恢复」这类动词**——数据层根本不支持，做出来就是骗人。
 */
export function PersonTable({
  data,
  query,
  page,
  setPage,
  onSelect,
  onPersonAction,
}: TableProps & {
  /** 人员的三个动作：修改（姓名/客户侧标识）/ 调岗（独立动作）/ 离职（一次性）。 */
  onPersonAction?: (mode: 'edit' | 'move' | 'leave') => (employeeNo: string) => void;
}): ReactNode {
  const list = data.people
    .filter((r) =>
      hit(query, r.employeeNo, r.name, r.externalRef, r.positionName, r.departmentName),
    )
    /* 排序按工号升序——它是锚点，也是这张表唯一稳定的顺序。 */
    .sort((a, b) => a.employeeNo.localeCompare(b.employeeNo));

  return (
    <>
      <table className="tb">
        <thead>
          <tr>
            <th>工号（锚点）</th>
            <th>姓名</th>
            <th>客户侧标识</th>
            <th>所属部门</th>
            <th>所属岗位</th>
            <th>在岗状态</th>
            <th>关联 ATC 数</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {slice(list, page).map((r) => (
            <tr key={r.employeeNo}>
              <td className="mono">
                <b>{r.employeeNo}</b>
              </td>
              <td>{r.name}</td>
              <td className="mono">{r.externalRef ?? '—'}</td>
              <td>{r.departmentName ?? '—'}</td>
              <td>
                {/* **只读投影。** 改岗位走「调岗」，不是改这一格。 */}
                {r.positionName ?? '未在岗'}
                <PersonHistory row={r} />
              </td>
              <td>
                {/*
                  在职 / 离职，**只有这两个**。`employedTo` 为空即在职。
                  它一次性写入，所以没有「恢复在职」这个动作。
                */}
                {r.employedTo === null ? (
                  <StatusBadge label="在职" tone="ok" />
                ) : (
                  <StatusBadge label={`离职 ${r.employedTo}`} tone="gry" />
                )}
              </td>
              <td className="mono">{r.atcIds.length}</td>
              <td>
                {/*
                  没有在岗岗位时**不给一个点了没反应的按钮**——那比不给更坏。
                  这个人还没有可跳转的去处，如实显示「—」。
                */}
                {r.positionId === null ? (
                  <span className="mono">—</span>
                ) : (
                  <ViewButton id={r.positionId} onSelect={onSelect} />
                )}
                {onPersonAction !== undefined && (
                  <button type="button" className="linkbtn" onClick={() => onPersonAction('edit')(r.employeeNo)}>
                    修改
                  </button>
                )}
                {/* 调岗与离职只在在职时有意义；离职一次性，没有反向动作 */}
                {onPersonAction !== undefined && r.employedTo === null && r.positionId !== null && (
                  <button type="button" className="linkbtn" onClick={() => onPersonAction('move')(r.employeeNo)}>
                    调岗
                  </button>
                )}
                {onPersonAction !== undefined && r.employedTo === null && (
                  <button type="button" className="linkbtn warn" onClick={() => onPersonAction('leave')(r.employeeNo)}>
                    离职
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager total={list.length} page={page} setPage={setPage} />
    </>
  );
}

/**
 * 在岗历史：全部区间，最近的在前。
 *
 * 「上季度是什么岗」是 AI 替代率趋势的前提——只留当前区间的话，
 * 上一季度的分母永远算不回来。
 *
 * 用 `<details>` 而不是自己管一个展开状态：它**不依赖 JavaScript**，
 * 因而在服务端渲染的判定里也能被验到。本目录不装 jsdom，
 * 自己管状态的展开只能靠浏览器实测守着，而那守不住回归。
 */
function PersonHistory({ row }: { row: PersonLedgerRow }): ReactNode {
  if (row.assignments.length === 0) return null;
  return (
    <details>
      <summary>在岗历史（{row.assignments.length} 段）</summary>
      <ul>
        {row.assignments.map((a) => (
          <li key={`${a.positionId}:${a.validFrom}`}>
            <span className="mono">
              {a.validFrom} — {a.validTo ?? '至今'}
            </span>{' '}
            {a.departmentName ?? '—'} · {a.positionName}
          </li>
        ))}
      </ul>
    </details>
  );
}
